import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Stack } from '@mui/material';

import { InfoField, InfoFieldChangeHandler } from '../../../../../../../components';

import { UTCToDateTime } from '../../../../../../../utils/dates';
import { useGetCountriesQuery } from '../../../../../../../api';
import { Gender, IdDocumentType } from '../../../../../../../enums';
import type { ProfileWithInfo } from '../../../../../../../types';

type NaturalDetailInfoProps = {
  profile: ProfileWithInfo | undefined;
  isEditMode: boolean;
  fields: Partial<ProfileWithInfo> | null;
  fieldsHandler: InfoFieldChangeHandler;
  isLoading?: boolean;
};

export const NaturalDetailInfo: FC<NaturalDetailInfoProps> = (props) => {
  const { isEditMode, profile, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // api
  const { data: countries } = useGetCountriesQuery();

  // elements
  const countrySelectOptions = countries?.countries.map(({ alpha2code, name }) => {
    return (
      <MenuItem key={alpha2code} value={alpha2code}>
        {name}
      </MenuItem>
    );
  });

  const genderSelectOptions = Object.values(Gender).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {t(`common.gender.${it}`)}
      </MenuItem>
    );
  });

  const documentSelectOptions = Object.values(IdDocumentType).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {t(`common.id-document-type.${it}`)}
      </MenuItem>
    );
  });

  return (
    <Stack direction="column" spacing={1}>
      <InfoField
        name={t('profile-details.detail.natural.citizenship-country')}
        value={profile?.info?.citizenship_country_code}
        formattedValue={
          profile?.info?.citizenship_country_code
            ? countries?.countries.find(
                (c) => c.alpha2code === profile.info?.citizenship_country_code
              )!.name
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'citizenship_country_code',
          value: fields?.info?.citizenship_country_code ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: countrySelectOptions,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.natural.first-name')}
        value={profile?.info?.first_name}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'first_name',
          value: fields?.info?.first_name ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.natural.last-name')}
        value={profile?.info?.last_name}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'last_name',
          value: fields?.info?.last_name ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.natural.gender')}
        value={profile?.info?.gender}
        formattedValue={
          profile?.info?.gender
            ? t(`common.gender.${Object.values(Gender).find((g) => g === profile.info?.gender)}`)
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'gender',
          value: fields?.info?.gender ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: genderSelectOptions,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.date-of-birth')}
        value={profile?.info?.date_of_birth}
        formattedValue={
          profile?.info?.date_of_birth
            ? UTCToDateTime(profile.info.date_of_birth as string)
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'date_of_birth',
          value: fields?.info?.date_of_birth ?? '',
          onChange: fieldsHandler,
          componentType: 'date',
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.natural.birth-country')}
        value={profile?.info?.birth_country_code}
        formattedValue={
          profile?.info?.birth_country_code
            ? countries?.countries.find((c) => c.alpha2code === profile.info?.birth_country_code)!
                .name
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'birth_country_code',
          value: fields?.info?.birth_country_code ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: countrySelectOptions,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.natural.place-of-birth')}
        value={profile?.info?.place_of_birth}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'place_of_birth',
          value: fields?.info?.place_of_birth ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.tax-residence-country')}
        value={profile?.info?.tax_residence_country_code}
        formattedValue={
          profile?.info?.tax_residence_country_code
            ? countries?.countries.find(
                (c) => c.alpha2code === profile.info?.tax_residence_country_code
              )!.name
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'tax_residence_country_code',
          value: fields?.info?.tax_residence_country_code ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: countrySelectOptions,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.tax-id-number')}
        value={profile?.info?.tax_id_number}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'tax_id_number',
          value: fields?.info?.tax_id_number ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.id-document-type')}
        value={profile?.info?.id_document_type}
        formattedValue={
          profile?.info?.id_document_type
            ? t(
                `common.id-document-type.${Object.values(IdDocumentType).find((type) => type === profile.info?.id_document_type)}`
              )
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'id_document_type',
          value: fields?.info?.id_document_type ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: documentSelectOptions,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.document-number')}
        value={profile?.info?.document_number}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'document_number',
          value: fields?.info?.document_number ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.personal-code')}
        value={profile?.info?.personal_code}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'personal_code',
          value: fields?.info?.personal_code ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.issuing-authority-name')}
        value={profile?.info?.issuing_authority_name}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'issuing_authority_name',
          value: fields?.info?.issuing_authority_name ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.issued-date')}
        value={profile?.info?.issued_date}
        formattedValue={
          profile?.info?.issued_date ? UTCToDateTime(profile.info.issued_date as string) : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'issued_date',
          value: fields?.info?.issued_date ?? '',
          onChange: fieldsHandler,
          componentType: 'date',
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.valid-to')}
        value={profile?.info?.valid_to}
        formattedValue={
          profile?.info?.valid_to ? UTCToDateTime(profile.info.valid_to as string) : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'valid_to',
          value: fields?.info?.valid_to ?? '',
          onChange: fieldsHandler,
          componentType: 'date',
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.natural.last-modified')}
        value={profile?.info?.row_start}
        formattedValue={
          profile?.info?.row_start ? UTCToDateTime(profile.info?.row_start as string) : undefined
        }
        withoutDivider
      />
    </Stack>
  );
};
